import { useCallback, useEffect, useState } from 'react';
import { App, Alert, Button, Drawer, Input, Space, Typography } from 'antd';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { getCapabilityProvider, putCapabilityBinding } from '@/api/capabilities';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { BindingView, FamilyCatalogueEntry, ProviderView } from '@/api/types';
import { TierBadge } from './tierBadge';

const { Paragraph, Text } = Typography;

/**
 * Bind/rebind the hub's slot for a capability family. There is no "list providers by family" endpoint
 * in the contract, so the admin names a providerId; we resolve it (get-by-id) to preview the descriptor
 * BEFORE binding — tier, mode, jurisdictions, evidence kinds, enabled. The PUT is the one write here;
 * 422 (unregistered / config-schema violation / missing entitlement) is rendered honestly.
 */
export function BindingDrawer({
  family,
  current,
  onClose,
  onBound,
}: {
  family: FamilyCatalogueEntry | null;
  current?: BindingView;
  onClose: () => void;
  onBound: () => void;
}) {
  const { message } = App.useApp();
  const [providerId, setProviderId] = useState('');
  const [configText, setConfigText] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ProviderView | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveMsg, setResolveMsg] = useState<string | null>(null);
  const [binding, setBinding] = useState(false);

  useEffect(() => {
    if (family) {
      setProviderId(current?.providerId ?? '');
      setConfigText(
        current?.config && Object.keys(current.config).length
          ? JSON.stringify(current.config, null, 2)
          : '',
      );
      setPreview(null);
      setResolveMsg(null);
      setConfigError(null);
    }
  }, [family, current]);

  const resolve = useCallback(async () => {
    if (!family || !providerId.trim()) return;
    setResolving(true);
    setResolveMsg(null);
    setPreview(null);
    try {
      const p = await getCapabilityProvider(family.family, family.version, providerId.trim());
      setPreview(p);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 404) setResolveMsg('No provider registered under that id for this family.');
      else if (isNotAvailable(n)) setResolveMsg('The provider registry is not available in this deployment.');
      else setResolveMsg(n.message);
    } finally {
      setResolving(false);
    }
  }, [family, providerId]);

  const bind = useCallback(async () => {
    if (!family) return;
    let config: Record<string, unknown> | undefined;
    const raw = configText.trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          config = parsed as Record<string, unknown>;
        } else {
          setConfigError('Config must be a JSON object.');
          return;
        }
      } catch {
        setConfigError('Config is not valid JSON.');
        return;
      }
    }
    setConfigError(null);
    setBinding(true);
    try {
      await putCapabilityBinding(family.family, family.version, {
        providerId: providerId.trim(),
        config,
      });
      message.success(`Bound ${family.family} → ${providerId.trim()}`);
      onBound();
      onClose();
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 422) {
        message.error(
          n.message ||
            'Bind refused: the provider is unregistered, the config violates its schema, or the hub lacks a required entitlement.',
        );
      } else if (isNotAvailable(n)) {
        message.info('The binding endpoint is not available in this deployment.');
      } else {
        message.error(n.message);
      }
    } finally {
      setBinding(false);
    }
  }, [family, providerId, configText, message, onBound, onClose]);

  return (
    <Drawer
      open={Boolean(family)}
      onClose={onClose}
      width={520}
      title={family ? `Bind ${family.family}` : 'Bind'}
    >
      {family && (
        <div>
          <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
            {family.description}{' '}
            <Text className="ol-mono" style={{ fontSize: 11 }}>
              ({family.family}.v{family.version})
            </Text>
          </Paragraph>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Provider id</div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                placeholder="e.g. graphintel-v1"
                onPressEnter={resolve}
              />
              <Button icon={<SearchOutlined />} onClick={resolve} loading={resolving}>
                Resolve
              </Button>
            </Space.Compact>
            <div style={{ fontSize: 11, color: 'var(--ol-text-tertiary)', marginTop: 4 }}>
              Resolve to preview the registered descriptor before binding.
            </div>
          </div>

          {resolveMsg && (
            <Alert type="warning" showIcon message={resolveMsg} style={{ marginBottom: 12 }} />
          )}

          {preview && (
            <div className="ol-card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Text strong style={{ fontSize: 13 }}>
                  {preview.displayName ?? preview.providerId}
                </Text>
                <TierBadge tier={preview.tier} />
              </div>
              {!preview.enabled && (
                <Alert
                  type="warning"
                  showIcon
                  banner
                  message="This provider is registered but not enabled — it cannot be bound until enabled."
                  style={{ marginBottom: 8 }}
                />
              )}
              <div className="ol-kv">
                <span className="ol-kv__k">Mode</span>
                <span className="ol-kv__v ol-mono">{preview.mode}</span>
                {preview.supportedJurisdictions && preview.supportedJurisdictions.length > 0 && (
                  <>
                    <span className="ol-kv__k">Jurisdictions</span>
                    <span className="ol-kv__v">{preview.supportedJurisdictions.join(', ')}</span>
                  </>
                )}
                {preview.emittedEvidenceKinds && preview.emittedEvidenceKinds.length > 0 && (
                  <>
                    <span className="ol-kv__k">Evidence</span>
                    <span className="ol-kv__v">{preview.emittedEvidenceKinds.join(', ')}</span>
                  </>
                )}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Tenant config (JSON)</div>
            <Input.TextArea
              value={configText}
              onChange={(e) => {
                setConfigText(e.target.value);
                if (configError) setConfigError(null);
              }}
              placeholder="{ }"
              autoSize={{ minRows: 3, maxRows: 12 }}
              className="ol-mono"
              status={configError ? 'error' : undefined}
            />
            {configError && (
              <Text type="danger" style={{ fontSize: 11.5 }}>
                {configError}
              </Text>
            )}
            <div style={{ fontSize: 11, color: 'var(--ol-text-tertiary)', marginTop: 4 }}>
              Validated server-side against the provider's schema-of-config at bind time.
            </div>
          </div>

          <Button
            type="primary"
            icon={<LinkOutlined />}
            loading={binding}
            disabled={!providerId.trim()}
            onClick={bind}
            block
          >
            {current ? 'Rebind' : 'Bind provider'}
          </Button>
        </div>
      )}
    </Drawer>
  );
}
